import React, { useRef, useEffect, useState, useCallback } from 'react';
import { EditorView, basicSetup } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { yaml } from '@codemirror/lang-yaml';
import { json } from '@codemirror/lang-json';
import { useVisualizationStore } from '../store/visualizationStore';

interface HighlightTokens {
  nodeTokens: string[];
  linkTokens: string[];
}

const LINK_VALUE_KEYS = new Set([
  'link',
  'links',
  'protocol',
  'bandwidth',
  'bandwidth_gbps',
  'bandwidth_gbytes',
  'bandwidth_gts',
  'latency',
  'scheme'
]);

const IGNORED_VALUE_KEYS = new Set([
  'description',
  'count',
  'notes',
  'note',
  'custom',
  'metadata'
]);

const IGNORED_TOKENS = new Set([
  'name',
  'id',
  'link',
  'links',
  'device',
  'devices',
  'instance',
  'instances',
  'component',
  'components',
  'description',
  'count',
  'scheme',
  'custom',
  'metadata',
  'ep1',
  'ep2'
]);

const MAX_LOOKBACK_LINES = 120;

const normalizeIdentifier = (rawValue?: string | null): string | null => {
  if (!rawValue) return null;
  const trimmed = rawValue
    .trim()
    .replace(/^['"]|['"]$/g, '')
    .replace(/[,;]+$/, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
  if (!trimmed) return null;
  if (!/[a-z0-9/_.-]/.test(trimmed)) {
    return null;
  }
  return trimmed;
};

const stripInlineComment = (value: string): string => {
  const hashIndex = value.indexOf('#');
  if (hashIndex >= 0) {
    return value.slice(0, hashIndex).trim();
  }
  const doubleSlashIndex = value.indexOf('//');
  if (doubleSlashIndex >= 0) {
    return value.slice(0, doubleSlashIndex).trim();
  }
  return value.trim();
};

const stripQuotesAndComma = (value: string): string => {
  let result = stripInlineComment(value);
  result = result.replace(/^['"]|['"]$/g, '');
  result = result.replace(/[,;]+$/, '');
  return result.trim();
};

const expandTokenVariants = (value?: string | null): string[] => {
  if (!value) return [];
  const variants = new Set<string>();
  const push = (v?: string | null) => {
    if (!v) return;
    const trimmed = v.trim();
    if (trimmed) {
      variants.add(trimmed);
    }
  };

  push(value);
  const cleaned = stripQuotesAndComma(value);
  push(cleaned);

  if (cleaned.includes('[')) {
    push(cleaned.slice(0, cleaned.indexOf('[')));
  }

  cleaned.split(/[./]/).forEach(part => {
    const trimmed = part.trim();
    if (trimmed) {
      push(trimmed);
    }
  });

  return Array.from(variants);
};

const getIndentSize = (lineText: string): number => {
  const match = lineText.match(/^\s*/);
  return match ? match[0].length : 0;
};

const parseLineForKeyValue = (lineText: string): { key?: string; value?: string } | null => {
  // eslint-disable-next-line no-useless-escape
  const keyValueMatch = lineText.match(/^\s*-?\s*"?([\w./\[\]-]+)"?\s*:\s*(.*)$/);
  if (keyValueMatch) {
    const key = keyValueMatch[1].toLowerCase();
    let value = stripQuotesAndComma(keyValueMatch[2]);
    if (!value) {
      return { key };
    }
    return { key, value };
  }

  const arrayValueMatch = lineText.match(/^\s*-\s*(.+)$/);
  if (arrayValueMatch) {
    const possibleValue = arrayValueMatch[1];
    if (!possibleValue.includes(':')) {
      const value = stripQuotesAndComma(possibleValue);
      if (value) {
        return { value };
      }
    }
  }

  return null;
};

const getAncestorSections = (state: EditorState, position: number): string[] => {
  if (!state.doc || typeof state.doc.lineAt !== 'function') {
    return [];
  }

  const ancestors: string[] = [];
  let cursor = Math.max(0, position);
  let currentIndent = getIndentSize(state.doc.lineAt(cursor).text);

  while (cursor >= 0) {
    const line = state.doc.lineAt(cursor);
    const trimmed = line.text.trim();
    const indent = getIndentSize(line.text);

    if (trimmed.endsWith(':')) {
      const key = trimmed.replace(/:.*/, '').replace(/['"]/g, '').replace(/^-/, '').trim();
      if (key && indent < currentIndent) {
        ancestors.push(key.toLowerCase());
        currentIndent = indent;
      }
    }

    if (line.from === 0) {
      break;
    }

    cursor = line.from - 1;
  }

  return ancestors;
};

const findNearestKeyValue = (
  state: EditorState,
  position: number,
  keys: string[],
  maxLines = MAX_LOOKBACK_LINES
): { key: string; value: string } | null => {
  if (!state.doc || typeof state.doc.lineAt !== 'function') {
    return null;
  }

  const keySet = new Set(keys.map(key => key.toLowerCase()));
  let cursor = Math.max(0, position);
  let linesChecked = 0;

  while (cursor >= 0 && linesChecked < maxLines) {
    const line = state.doc.lineAt(cursor);
    const parsed = parseLineForKeyValue(line.text);

    if (parsed?.key && parsed.value && keySet.has(parsed.key.toLowerCase())) {
      return { key: parsed.key.toLowerCase(), value: parsed.value };
    }

    if (line.from === 0) {
      break;
    }

    cursor = line.from - 1;
    linesChecked += 1;
  }

  return null;
};

const determineValueCategory = (key: string | undefined, ancestors: string[]): 'node' | 'link' | null => {
  if (!key) {
    return ancestors.includes('links') ? 'link' : 'node';
  }

  const normalizedKey = key.toLowerCase();

  if (IGNORED_VALUE_KEYS.has(normalizedKey)) {
    return null;
  }

  if (LINK_VALUE_KEYS.has(normalizedKey)) {
    return 'link';
  }

  if (normalizedKey === 'name' && ancestors.includes('links')) {
    return 'link';
  }

  return 'node';
};

const collectHighlightTokens = (state: EditorState): HighlightTokens => {
  const safeDoc = state.doc as any;
  if (!safeDoc || typeof safeDoc.lineAt !== 'function' || !state.selection) {
    return { nodeTokens: [], linkTokens: [] };
  }

  const selection = state.selection.main;
  const ancestors = getAncestorSections(state, selection.head);
  const nodeTokens = new Set<string>();
  const linkTokens = new Set<string>();
  const addToken = (rawValue?: string | null, target: 'node' | 'link' = 'node') => {
    if (!rawValue) return;
    const variants = expandTokenVariants(rawValue);
    variants.forEach(variant => {
      const normalized = normalizeIdentifier(variant);
      if (!normalized || IGNORED_TOKENS.has(normalized)) {
        return;
      }
      const hasAlpha = /[a-z]/.test(normalized);
      const hasDivider = /[./_-]/.test(normalized);
      if (!hasAlpha && !hasDivider) {
        return;
      }
      if (target === 'link') {
        linkTokens.add(normalized);
      } else {
        nodeTokens.add(normalized);
      }
    });
  };

  if (!selection.empty && typeof (state as any).sliceDoc === 'function') {
    const selectedText = state.sliceDoc(selection.from, selection.to);
    const category = ancestors.includes('links') ? 'link' : 'node';
    addToken(selectedText, category);
  }

  if (selection.empty && typeof (state as any).wordAt === 'function' && typeof (state as any).sliceDoc === 'function') {
    const word = state.wordAt(selection.head);
    if (word) {
      addToken(state.sliceDoc(word.from, word.to), ancestors.includes('links') ? 'link' : 'node');
    }
  }

  const currentLine = state.doc.lineAt(selection.head);
  const lineInfo = parseLineForKeyValue(currentLine.text);
  if (lineInfo?.value) {
    const category = determineValueCategory(lineInfo.key, ancestors);
    if (category) {
      addToken(lineInfo.value, category);
    }
  }

  const contextEntry = findNearestKeyValue(
    state,
    selection.head,
    ['name', 'id', 'device', 'instance', 'site', 'pod', 'network', 'rack', 'link']
  );
  if (contextEntry) {
    const category = determineValueCategory(contextEntry.key, ancestors);
    if (category) {
      addToken(contextEntry.value, category);
    }
  }

  return {
    nodeTokens: Array.from(nodeTokens).sort(),
    linkTokens: Array.from(linkTokens).sort()
  };
};

const areTokenArraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

// Custom light theme for the editor matching navigation panel colors
const customTheme = EditorView.theme({
  '&': {
    height: '100%',
    backgroundColor: '#ffffff',
    fontFamily: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
    fontSize: '0.875rem', // text-sm equivalent (14px)
    color: '#111827', // text-gray-900
  },
  '.cm-scroller': {
    overflow: 'auto',
  },
  '.cm-gutters': {
    backgroundColor: '#f8fafc',
    borderRight: '1px solid #e2e8f0',
    fontSize: '0.75rem', // text-xs equivalent (12px)
    color: '#4b5563', // text-gray-600
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'transparent',
  },
  '.cm-activeLine': {
    backgroundColor: 'transparent !important',
  },
  '.cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, &.cm-focused .cm-selectionBackground': {
    backgroundColor: 'rgba(59, 130, 246, 0.35) !important', // blue-500 at 35% opacity
  },
  '.cm-content ::selection': {
    backgroundColor: 'rgba(59, 130, 246, 0.35)',
  },
  '.cm-content': {
    fontFamily: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
    fontSize: '0.875rem', // text-sm equivalent
  },
  '.cm-line': {
    color: '#111827', // text-gray-900
  },
  // Syntax highlighting matching app color palette
  '.cm-keyword': {
    color: '#3b82f6', // blue-500 (matches primary color)
  },
  '.cm-string': {
    color: '#22c55e', // green-500 (matches success color)
  },
  '.cm-number': {
    color: '#a855f7', // purple-500 (matches accent color)
  },
  '.cm-bool, .cm-null': {
    color: '#ef4444', // red-500 (matches error/alert color)
  },
  '.cm-propertyName': {
    color: '#374151', // gray-700 (matches heading color)
  },
  '.cm-comment': {
    color: '#9ca3af', // gray-400
    fontStyle: 'italic',
  },
  '.cm-punctuation, .cm-bracket': {
    color: '#6b7280', // gray-500
  },
  '.cm-operator': {
    color: '#4b5563', // gray-600
  },
}, { dark: false });

interface BreadcrumbItem {
  label: string;
  position: number; // Line position to jump to
}

export const TopologyEditorPanel: React.FC = () => {
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const lastTokensRef = useRef<HighlightTokens>({ nodeTokens: [], linkTokens: [] });

  const {
    editorVisible,
    editorContent,
    editorFormat,
    editorDirty,
    editorWidth,
    infrastructure,
    toggleEditor,
    setEditorFormat,
    setEditorWidth,
    updateEditorContent,
    applyEditorChanges,
    revertEditorChanges,
    setEditorHighlights,
    editorHighlightEnabled,
    setEditorHighlightEnabled,
  } = useVisualizationStore();

  const [isResizing, setIsResizing] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  const updateHighlightTokens = useCallback((state: EditorState | null) => {
    if (!state) {
      if (lastTokensRef.current.nodeTokens.length || lastTokensRef.current.linkTokens.length) {
        lastTokensRef.current = { nodeTokens: [], linkTokens: [] };
        setEditorHighlights([], []);
      }
      return;
    }

    const tokens = collectHighlightTokens(state);
    if (
      !areTokenArraysEqual(tokens.nodeTokens, lastTokensRef.current.nodeTokens) ||
      !areTokenArraysEqual(tokens.linkTokens, lastTokensRef.current.linkTokens)
    ) {
      lastTokensRef.current = tokens;
      setEditorHighlights(tokens.nodeTokens, tokens.linkTokens);
    }
  }, [setEditorHighlights]);

  // Update breadcrumbs based on cursor position
  const updateBreadcrumbs = useCallback((state: EditorState | null) => {
    if (!state || !state.doc || typeof state.doc.lineAt !== 'function') {
      setBreadcrumbs([]);
      return;
    }

    const selection = state.selection.main;
    const position = selection.head;
    const breadcrumbItems: BreadcrumbItem[] = [];

    // Always start with topology name
    if (infrastructure?.name) {
      breadcrumbItems.push({
        label: infrastructure.name,
        position: 0
      });
    }

    // Build a path of ancestors by walking backwards from current position
    // collecting sections and names in proper hierarchy
    const pathItems: { label: string; position: number; indent: number; isSection: boolean }[] = [];

    let searchCursor = position;
    let currentIndent = getIndentSize(state.doc.lineAt(position).text);
    let linesSearched = 0;
    const maxSearchLines = 200;
    let firstIteration = true;

    while (searchCursor >= 0 && linesSearched < maxSearchLines) {
      const line = state.doc.lineAt(searchCursor);
      const lineIndent = getIndentSize(line.text);
      const trimmed = line.text.trim();

      // On first iteration, also check current line for name
      const shouldCheck = firstIteration ? lineIndent <= currentIndent : lineIndent < currentIndent;

      if (shouldCheck) {
        // Check if it's a section header
        if (trimmed.endsWith(':')) {
          const key = trimmed.replace(/:.*/, '').replace(/['"]/g, '').replace(/^-/, '').trim().toLowerCase();
          if (key) {
            pathItems.push({
              label: key,
              position: line.from,
              indent: lineIndent,
              isSection: true
            });
            currentIndent = lineIndent;
          }
        }

        // Check if it's a name field (item name like device name, component name)
        const parsed = parseLineForKeyValue(line.text);
        if (parsed?.key === 'name' && parsed.value) {
          pathItems.push({
            label: parsed.value,
            position: line.from,
            indent: lineIndent,
            isSection: false
          });
          currentIndent = lineIndent;
        }
      }

      firstIteration = false;

      if (line.from === 0) {
        break;
      }

      searchCursor = line.from - 1;
      linesSearched++;
    }

    // Reverse to get root-to-leaf order
    pathItems.reverse();

    // Add topology name at the beginning if available
    // then add all path items
    pathItems.forEach(item => {
      breadcrumbItems.push({
        label: item.label,
        position: item.position
      });
    });

    setBreadcrumbs(breadcrumbItems);
  }, [infrastructure]);

  // Handle breadcrumb click - jump to that position
  const handleBreadcrumbClick = (position: number) => {
    const view = viewRef.current;
    if (!view) return;

    view.dispatch({
      selection: { anchor: position },
      scrollIntoView: true
    });
    view.focus();
  };

  // Handle panel resize
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle file download
  const handleDownload = () => {
    if (!editorContent) return;

    // Use topology name from infrastructure, or default to 'topology'
    const topologyName = infrastructure?.name || 'topology';

    const blob = new Blob([editorContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${topologyName}.${editorFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!editorVisible) {
      updateHighlightTokens(null);
    }
  }, [editorVisible, updateHighlightTokens]);

  useEffect(() => {
    return () => {
      updateHighlightTokens(null);
    };
  }, [updateHighlightTokens]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      setEditorWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, setEditorWidth]);

  // Initialize CodeMirror editor
  useEffect(() => {
    if (!editorRef.current || !editorVisible) return;

    const startState = EditorState.create({
      doc: editorContent || '',
      extensions: [
        basicSetup,
        (editorFormat === 'yaml' ? yaml() : json()),
        customTheme,
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            updateEditorContent(update.state.doc.toString());
          }
          if (update.selectionSet || update.docChanged) {
            updateHighlightTokens(update.state);
            updateBreadcrumbs(update.state);
          }
        }),
      ],
    });

    const view = new EditorView({
      state: startState,
      parent: editorRef.current,
    });

    viewRef.current = view;
    updateHighlightTokens(startState);
    updateBreadcrumbs(startState);

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorVisible, editorFormat, updateEditorContent, updateHighlightTokens, updateBreadcrumbs]);

  // Update editor content when it changes externally
  useEffect(() => {
    if (viewRef.current && editorContent !== null) {
      const currentDoc = viewRef.current.state.doc.toString();
      if (currentDoc !== editorContent) {
        viewRef.current.dispatch({
          changes: {
            from: 0,
            to: currentDoc.length,
            insert: editorContent,
          },
        });
      }
    }
  }, [editorContent]);

  if (!editorVisible) {
    return null;
  }

  return (
    <div
      className="fixed top-0 right-0 h-screen bg-white border border-gray-200 shadow-xl flex flex-col z-50 rounded-l-2xl"
      style={{ width: `${editorWidth}px` }}
    >
      {/* Resize Handle */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 bg-gray-200 hover:bg-gray-400 cursor-col-resize transition-colors z-10 ${
          isResizing ? 'bg-gray-400' : ''
        }`}
        onMouseDown={handleMouseDown}
        title="Drag to resize"
      />

      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-gray-200 flex items-center justify-between rounded-tl-2xl">
        <div className="flex items-center space-x-4">
          <h3 className="text-gray-800 font-semibold">Topology Editor</h3>

          {/* Format Toggle */}
          <div className="flex bg-white border border-gray-200 rounded">
            <button
              className={`px-3 py-1 text-sm rounded-l ${
                editorFormat === 'yaml'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setEditorFormat('yaml')}
            >
              YAML
            </button>
            <button
              className={`px-3 py-1 text-sm rounded-r ${
                editorFormat === 'json'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setEditorFormat('json')}
            >
              JSON
            </button>
          </div>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-1 shadow-sm"
            title="Download topology file"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>Download</span>
          </button>

          {/* Highlight Toggle */}
          <label className="flex items-center space-x-2 text-sm text-gray-600">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={editorHighlightEnabled}
              onChange={(e) => setEditorHighlightEnabled(e.target.checked)}
            />
            <span>Highlight matches</span>
          </label>
        </div>

        {/* Close Button */}
        <button
          onClick={toggleEditor}
          className="text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Close editor"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center space-x-1 text-sm overflow-x-auto">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <button
                onClick={() => handleBreadcrumbClick(crumb.position)}
                className={`px-2 py-1 rounded hover:bg-gray-200 transition-colors whitespace-nowrap ${
                  index === breadcrumbs.length - 1
                    ? 'text-blue-600 font-medium'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                title={`Jump to ${crumb.label}`}
              >
                {crumb.label}
              </button>
              {index < breadcrumbs.length - 1 && (
                <svg
                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Editor */}
      <div
        className="flex-1 overflow-auto"
        ref={editorRef}
        style={{ minHeight: 0 }}
      />

      {/* Status Bar & Actions */}
      <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 rounded-bl-2xl">
        {editorDirty && (
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm font-medium text-amber-700">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs uppercase tracking-wide">Unsaved</span>
              <span className="ml-3 text-gray-700">Changes pending</span>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={revertEditorChanges}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 transition-colors shadow-sm"
              >
                Revert
              </button>
              <button
                onClick={applyEditorChanges}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors font-semibold shadow-sm"
              >
                Apply Changes
              </button>
            </div>
          </div>
        )}

        {!editorDirty && (
          <div className="text-sm flex items-center font-medium text-green-700">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs uppercase tracking-wide">Saved</span>
            <span className="ml-3 text-gray-700">No unsaved changes</span>
          </div>
        )}
      </div>
    </div>
  );
};
